import React, { useCallback, useEffect, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'external/infiniteScroll/app'
import { DownloadBoxIcon } from 'images'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import ArchivedActions, { thunks as ArchivedThunks } from 'reducers/Archived'
import { CollectionContent, DialogModal, ReleasesTable } from 'components'
import ArchivedReleaseModal from 'components/Modals/ArchivedReleaseModal'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { useResponsiveBreakpointsVersion } from 'contexts/ResponsiveBreakpointsContext'
import archivedStyles from './Archived.module.scss'

export const ArchivedContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const didLoadRef = useRef(false)
  const [showClearListModal, setShowClearListModal] = useState(false)
  const { releases, page, totalItemsCount, loading, format } = useSelector(
    ({ archived, profile }) => ({
      releases: archived.releases,
      page: archived.page,
      totalItemsCount: archived.totalItemsCount,
      loading: archived.loading,
      format: profile.profile.format
    }),
    shallowEqual
  )

  const onLoadMore = useCallback(() => {
    if (!loading) {
      dispatch(ArchivedThunks.search(page + 1))
    }
  }, [dispatch, loading, page])

  useEffect(() => {
    if (didLoadRef.current) {
      return
    }

    didLoadRef.current = true

    if (page === 0) {
      dispatch(ArchivedThunks.search(1))
    } else {
      dispatch(ArchivedThunks.refreshList())
    }
  }, [dispatch, page])

  const handlePlayRelease = useCallback(
    (release) => {
      dispatch(
        PlayQueueThunks.playRelease(release.releaseid, {
          source: PLAY_SOURCES.ARCHIVED,
          trigger: PLAY_TRIGGERS.ROW
        })
      )
    },
    [dispatch]
  )

  const handleDownloadRelease = useCallback(
    (release) => {
      dispatch(
        DownloadsThunks.addReleaseToDownloadQueue(
          release,
          format,
          'archived'
        )
      )
    },
    [dispatch, format]
  )

  const handleRestoreRelease = useCallback(
    (release) => {
      dispatch(
        ArchivedThunks.restoreRelease(release.releaseid, () => {
          dispatch(ArchivedActions.removeArchivedRelease(release))
        })
      )
    },
    [dispatch]
  )

  const handleOpenClearListModal = useCallback(() => {
    setShowClearListModal(true)
  }, [])

  const handleCloseClearListModal = useCallback(() => {
    setShowClearListModal(false)
  }, [])

  const handleClearAll = useCallback(() => {
    dispatch(ArchivedThunks.clearAll())
    setShowClearListModal(false)
  }, [dispatch])

  const hasMorePage =
    totalItemsCount === null || totalItemsCount > releases.length

  const modalProps = {
    handleDownloadClick: handleDownloadRelease,
    handleRestoreClick: handleRestoreRelease,
    handlePlayRelease
  }

  const renderClearListModal = () => {
    return (
      <DialogModal
        title={t('collections.restore-all-releases')}
        onClickYes={handleClearAll}
        onClickCancel={handleCloseClearListModal}>
        <div className="w-full flex flex-col items-center my-10 text-sm font-header">
          <div className="mb-2">{t('collections.are-you-sure')}</div>
          <div>
            {t('collections.archived-modal-subtext', {
              count: releases.length
            })}
          </div>
        </div>
      </DialogModal>
    )
  }

  const renderTable = () => {
    return (
      <div id="archivedScrollableDiv" className={archivedStyles.scrollableDiv}>
        <InfiniteScroll
          hasChildren
          dataLength={releases.length}
          hasMore={hasMorePage}
          loader={
            <h4 className="px-4 py-4 text-white">
              {t('home.release-loading')}
            </h4>
          }
          next={onLoadMore}
          scrollableTarget="archivedScrollableDiv">
          <ReleasesTable
            downloadSource="archived_releases_table"
            playSource={PLAY_SOURCES.ARCHIVED}
            items={releases}
            modalComponent={ArchivedReleaseModal}
            modalProps={modalProps} />
        </InfiniteScroll>
      </div>
    )
  }

  return (
    <div
      className="flex h-full flex-col"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.ARCHIVED}>
      <CollectionContent
        disableContentScroll
        actionTitle={t('collections.restore-all')}
        icon={<DownloadBoxIcon />}
        table={renderTable()}
        title={t('collections.archived')}
        actionCallback={handleOpenClearListModal}
        actionDisabled={releases.length === 0} />
      {showClearListModal && renderClearListModal()}
    </div>
  )
}

export default ArchivedContent
